import React, { useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { PictureAsPdfOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import { useLazyListShipmentMessagesQuery } from "store/services/smsLogListApi";

const buildFilename = (filters) => {
  const parts = ["sms-log-report"];
  if (filters.fromDate) parts.push(`from-${filters.fromDate}`);
  if (filters.toDate) parts.push(`to-${filters.toDate}`);
  if (filters.messageType) parts.push(filters.messageType.toLowerCase());
  parts.push(moment().format("YYYYMMDD-HHmm"));
  return `${parts.join("_")}.pdf`;
};

const ExportSmsLogList = ({ filters = {}, disabled = false }) => {
  const [loading, setLoading] = useState(false);
  const [fetchMessages] = useLazyListShipmentMessagesQuery();

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 100000 };
      if (filters.keyword) params.keyword = filters.keyword;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      if (filters.messageType) params.messageType = filters.messageType;

      const res = await fetchMessages(params).unwrap();

      const rows = (res?.data ?? []).map((m) => [
        m.sourceModel || "-",
        m.shipmentId || m.codTransferId || "-",
        m.messageType || "-",
        m.message || "-",
        m.createdAt ? moment(m.createdAt).format("MM/DD/YYYY hh:mm A") : "-",
      ]);

      const currentMonthTotal = res?.summary?.currentMonthTotal ?? 0;
      const lastMonthTotal = res?.summary?.lastMonthTotal ?? 0;
      const totalItems = res?.totalItems ?? rows.length;

      // Group counts by message type
      const typeCounts = (res?.data ?? []).reduce((acc, m) => {
        const type = m.messageType || "Unknown";
        if (!acc[type]) acc[type] = { lastMonth: 0, currentMonth: 0 };
        const createdAt = moment(m.createdAt);
        if (createdAt.isSame(moment(), "month")) {
          acc[type].currentMonth++;
        } else if (createdAt.isSame(moment().subtract(1, "month"), "month")) {
          acc[type].lastMonth++;
        }
        return acc;
      }, {});

      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(16).setTextColor("#000");
      doc.text("SMS Log Summary Report", pageWidth / 2, 40, { align: "center" });

      const subLines = [];
      if (filters.fromDate && filters.toDate)
        subLines.push(`Date Range: ${filters.fromDate} - ${filters.toDate}`);
      if (filters.messageType) subLines.push(`Message Type: ${filters.messageType}`);
      if (subLines.length) {
        doc.setFontSize(9).setTextColor("#666");
        doc.text(subLines.join("   |   "), pageWidth / 2, 56, { align: "center" });
      }

      // Type breakdown table
      const tableRows = Object.entries(typeCounts).map(([type, counts]) => [
        type,
        counts.lastMonth,
        counts.currentMonth,
      ]);

      // Add total row
      const totalLastMonth = Object.values(typeCounts).reduce((sum, c) => sum + c.lastMonth, 0);
      const totalCurrentMonth = Object.values(typeCounts).reduce((sum, c) => sum + c.currentMonth, 0);
      tableRows.push([
        { content: "Total", styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
        { content: totalLastMonth, styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
        { content: totalCurrentMonth, styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
      ]);

      autoTable(doc, {
        startY: 80,
        head: [["Message Type", "Last Month Count", "Current Month Count"]],
        body: tableRows.length ? tableRows : [["-", "0", "0"]],
        styles: { fontSize: 10, cellPadding: 6, halign: "center" },
        headStyles: { fillColor: [33, 65, 130], textColor: 255, fontSize: 11 },
        margin: { left: 40, right: 40 },
        didDrawPage: (data) => {
          const str = `Total: ${totalItems}`;
          doc.setFontSize(8).setTextColor("#999");
          doc.text(
            str,
            doc.internal.pageSize.getWidth() - 40,
            doc.internal.pageSize.getHeight() - 20,
            { align: "right" }
          );
        },
      });

      doc.save(buildFilename(filters));
    } catch (err) {
      toast.error("Could not export SMS log report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

    return (
        <Button
            variant="outlined"
            size="small"
            color="error"
            onClick={handleExport}
            disabled={disabled || loading}
            startIcon={
                loading ? (
                    <CircularProgress size={16} color="inherit" />
                ) : (
                    <PictureAsPdfOutlined fontSize="small" />
                )
            }
        >
            {loading ? "Generating..." : "Export PDF"}
        </Button>
    );
};

export default ExportSmsLogList;